import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Button,
  Divider,
  Switch,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { collection, query, where, getDocs, updateDoc, doc, Timestamp, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Employee } from '../types/employee';

interface Category {
  id: string;
  name: string;
  displayName?: string;
}

/**
 * スタッフ通知設定画面
 * カテゴリ別のオーダー通知設定
 */
const StaffNotificationSettings: React.FC = () => {
  const { user, shop } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [callNotificationEnabled, setCallNotificationEnabled] = useState(true);

  /**
   * スタッフ情報とカテゴリを取得
   */
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.uid || !shop?.id) return;

      try {
        setLoading(true);

        // スタッフ情報を取得
        const employeesQuery = query(
          collection(db, 'employees'),
          where('userId', '==', user.uid),
          where('status', '==', 'active')
        );
        const employeesSnap = await getDocs(employeesQuery);

        if (employeesSnap.empty) {
          setError('スタッフ情報が見つかりません');
          return;
        }

        const employeeDoc = employeesSnap.docs[0];
        const employeeData = {
          id: employeeDoc.id,
          ...employeeDoc.data(),
          personalInfo: {
            ...employeeDoc.data().personalInfo,
            birthDate: employeeDoc.data().personalInfo?.birthDate?.toDate?.() || new Date(),
          },
          employmentInfo: {
            ...employeeDoc.data().employmentInfo,
            hireDate: employeeDoc.data().employmentInfo?.hireDate?.toDate?.() || new Date(),
            graduationDate: employeeDoc.data().employmentInfo?.graduationDate?.toDate?.(),
          },
          createdAt: employeeDoc.data().createdAt?.toDate?.() || new Date(),
          updatedAt: employeeDoc.data().updatedAt?.toDate?.() || new Date(),
        } as Employee;

        setEmployee(employeeData);

        // 現在の通知設定カテゴリを取得
        const currentCategories = employeeData.notificationSettings?.orderNotificationCategories || [];
        setSelectedCategories(currentCategories);

        // 呼び出し通知設定を取得
        const callEnabled = employeeData.notificationSettings?.callNotificationEnabled ?? true;
        setCallNotificationEnabled(callEnabled);

        // 店舗のカテゴリを取得
        const categoriesQuery = query(
          collection(db, 'productCategories'),
          where('shopId', '==', shop.id)
        );
        const categoriesSnap = await getDocs(categoriesQuery);

        const categoriesData: Category[] = categoriesSnap.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name || '',
          displayName: doc.data().displayName || doc.data().name || '',
        }));

        setCategories(categoriesData);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError('データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, shop]);

  /**
   * カテゴリ選択変更
   */
  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  /**
   * 全選択/全解除
   */
  const handleSelectAll = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(categories.map((c) => c.id));
    }
  };

  /**
   * 保存
   */
  const handleSave = async () => {
    if (!employee) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await updateDoc(doc(db, 'employees', employee.id), {
        'notificationSettings.orderNotificationCategories': selectedCategories,
        'notificationSettings.callNotificationEnabled': callNotificationEnabled,
        updatedAt: Timestamp.now(),
      });

      setSuccess('通知設定を保存しました');
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!employee) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">スタッフ情報が見つかりません</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <NotificationsIcon />
        通知設定
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        オーダー通知を受け取るカテゴリを選択してください。
        選択したカテゴリの商品がオーダーされた時のみ通知されます。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* 呼び出し通知設定 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            呼び出し通知
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            お客様がテーブルから呼び出しボタンを押した時の通知設定
          </Typography>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={callNotificationEnabled}
                onChange={(e) => setCallNotificationEnabled(e.target.checked)}
                color="primary"
              />
            }
            label={callNotificationEnabled ? '呼び出し通知を受け取る' : '呼び出し通知OFF'}
          />
        </CardContent>
      </Card>

      {/* カテゴリ別オーダー通知設定 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              オーダー通知（カテゴリ別）
            </Typography>
            <Button size="small" onClick={handleSelectAll}>
              {selectedCategories.length === categories.length ? '全解除' : '全選択'}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {employee.personalInfo.lastName} {employee.personalInfo.firstName} さんの設定
          </Typography>

          <Divider sx={{ my: 2 }} />

          {categories.length === 0 ? (
            <Alert severity="info">カテゴリが登録されていません</Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                通知を受け取るカテゴリ（{selectedCategories.length}/{categories.length}）
              </Typography>

              <FormGroup sx={{ mt: 2 }}>
                {categories.map((category) => (
                  <FormControlLabel
                    key={category.id}
                    control={
                      <Checkbox
                        checked={selectedCategories.includes(category.id)}
                        onChange={() => handleCategoryToggle(category.id)}
                      />
                    }
                    label={category.displayName || category.name}
                  />
                ))}
              </FormGroup>
            </>
          )}
        </CardContent>
      </Card>

      {!employee.currentWorkStatus?.isWorking && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          現在退勤中です。出勤するまで通知は届きません。
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          保存
        </Button>
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2" gutterBottom fontWeight="bold">
          通知の仕組み
        </Typography>
        <Typography variant="caption" component="div">
          • 出勤中のみ通知が届きます
        </Typography>
        <Typography variant="caption" component="div">
          • 呼び出し通知: お客様がテーブルから呼び出しボタンを押した時に通知されます
        </Typography>
        <Typography variant="caption" component="div">
          • オーダー通知: 選択したカテゴリの商品がオーダーされた時のみ通知されます
        </Typography>
        <Typography variant="caption" component="div">
          • 例：ドリンクカテゴリのみ選択した場合、ドリンクのオーダーのみ通知されます
        </Typography>
      </Alert>
    </Container>
  );
};

export default StaffNotificationSettings;
